import { useCallback, useEffect, useMemo, useState } from 'react';
import { Navigate, useNavigate, useParams } from 'react-router-dom';
import { signInPath } from 'common/config/urls';
import {
  AUTH_CONTACT_US_URL,
  AUTH_ENABLE_CONTACT_US_LINK,
  COUNTDOWN_INTERVAL,
  INITIAL_COUNTDOWN,
  SIGN_IN_VIEW,
} from 'common/constants/auth';
import { LoadingState, Text, TextLink } from 'components/ui';
import { useCountdown } from 'components/ui/hooks';
import { buildResendActivationUrl } from 'pages/Auth/ActivateAccount/ResendActivation';
import { AuthFormHeader, AuthPageStack, AuthTitleText } from 'pages/Auth/components';
import { useActivateUser, useAuthSession } from 'pages/Auth/hooks';
import { buildSignInUrl } from 'pages/Auth/utils';
import {
  ACTIVATE_ALREADY_ACTIVE_MESSAGE,
  ACTIVATE_ALREADY_ACTIVE_TITLE,
  ACTIVATE_ALREADY_USED_MESSAGE,
  ACTIVATE_CONTACT_US_LABEL,
  ACTIVATE_HELP_PROMPT,
  ACTIVATE_INVALID_LINK_MESSAGE,
  ACTIVATE_LATER_ERROR_MESSAGE,
  ACTIVATE_LOADING_LABEL,
  ACTIVATE_TITLE_LEAD,
  ACTIVATE_TITLE_TAIL,
} from './ActivateAccount.constants';
import { ActivateAccountMessage } from './ActivateAccount.types';
import { isActivationClientError, normalizePasswordResetUrl } from './ActivateAccount.utils';
import { ActivateAccountStatusStep } from './components';

const resendActivationHref = buildResendActivationUrl();

const ActivateAccount = () => {
  const navigate = useNavigate();
  const activateUser = useActivateUser({ useGlobalLoading: false });
  const { token, uid } = useParams<{ token: string; uid: string }>();
  const [isLoading, setIsLoading] = useState(true);
  const [message, setMessage] = useState<ActivateAccountMessage | null>(null);
  const { hasSession } = useAuthSession();
  const showContactLink = AUTH_ENABLE_CONTACT_US_LINK;
  const handleSuccessRedirect = useCallback(() => {
    navigate(signInPath, { replace: true });
  }, [navigate]);
  const countdown = useCountdown({
    enabled: !isLoading && message?.severity === 'success' && !hasSession,
    initialCountdown: INITIAL_COUNTDOWN,
    intervalMs: COUNTDOWN_INTERVAL,
    onComplete: handleSuccessRedirect,
  });

  // Activation is a fire-once flow: the token is either consumed here or it hands the user off to
  // password setup when the backend marks the invited account as not fully initialized yet.
  useEffect(() => {
    let isMounted = true;

    const activateAccount = async () => {
      if (!uid || !token) {
        if (isMounted) {
          setIsLoading(false);
          setMessage({
            severity: 'error',
            text: ACTIVATE_INVALID_LINK_MESSAGE,
          });
        }
        return;
      }

      const [response, error] = await activateUser(uid, token);

      if (!isMounted) {
        return;
      }

      if (error) {
        setIsLoading(false);
        setMessage({
          severity: 'error',
          text: isActivationClientError(error)
            ? ACTIVATE_ALREADY_USED_MESSAGE
            : ACTIVATE_LATER_ERROR_MESSAGE,
        });
        return;
      }

      // Invited users who do not have a usable password continue into the password setup flow
      // instead of seeing the generic activation success state.
      if (response?.requires_password_setup && response.password_reset_url) {
        navigate(normalizePasswordResetUrl(response.password_reset_url), { replace: true });
        return;
      }

      setIsLoading(false);
      setMessage(
        response?.already_activated
          ? {
            severity: 'success',
            text: ACTIVATE_ALREADY_ACTIVE_MESSAGE,
            title: ACTIVATE_ALREADY_ACTIVE_TITLE,
          }
          : {
            severity: 'success',
            text: '',
          }
      );
    };

    activateAccount();

    return () => {
      isMounted = false;
    };
  }, [activateUser, navigate, token, uid]);

  const hasLoggedInUser = useMemo(
    () => hasSession && !isLoading && message?.severity === 'success',
    [hasSession, isLoading, message]
  );

  if (hasLoggedInUser) {
    return <Navigate replace to={buildSignInUrl({ view: SIGN_IN_VIEW.WORKSPACE_SELECTION })} />;
  }

  return (
    <AuthPageStack>
      <AuthFormHeader title={<AuthTitleText lead={ACTIVATE_TITLE_LEAD} tail={ACTIVATE_TITLE_TAIL} />} />
      {isLoading ? <LoadingState label={ACTIVATE_LOADING_LABEL} /> : null}
      {!isLoading ? (
        <ActivateAccountStatusStep
          contactAction={showContactLink && message?.severity !== 'success' ? (
            <Text color="text.secondary" textAlign="center" variant="body2">
              {ACTIVATE_HELP_PROMPT}{' '}
              <TextLink
                href={AUTH_CONTACT_US_URL}
                rel="noopener noreferrer"
                target="_blank"
                variant="body2"
                visualVariant="emphasis"
              >
                {ACTIVATE_CONTACT_US_LABEL}
              </TextLink>
            </Text>
          ) : null}
          countdown={message?.severity === 'success' ? countdown : undefined}
          message={message}
          resendActivationHref={message?.severity !== 'success' ? resendActivationHref : undefined}
          signInHref={signInPath}
        />
      ) : null}
    </AuthPageStack>
  );
};

export default ActivateAccount;
